// src/pages/Mcq.jsx
import React, { useState, useRef, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import "../styles/Mcq.css";
import { FaBell, FaUserCircle } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";

const apiBase = "https://digital.tifleducation.com/admin/public/api";
const LOCAL_SELECTED_KEY = "selectedQuestionsByChapter"; // chapter -> { mcq: [ids] }

const Mcq = () => {
  const navigate = useNavigate();
  const [activeItem, setActiveItem] = useState("papers");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const location = useLocation();
  const state = location.state || {};

  const [user, setUser] = useState({ id: null, name: "", email: "", password: "" });
  const [questions, setQuestions] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState({});
  const [mcqLimit, setMcqLimit] = useState(0);

  // Toggle dropdown
  const toggleDropdown = () => setShowDropdown((prev) => !prev);

  // Fetch MCQ questions
  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const token = localStorage.getItem("token");

        // read limit from localStorage form (regularPaperForm)
        const regularForm = JSON.parse(localStorage.getItem("regularPaperForm") || "{}");
        const limit = Number(regularForm.mcq || 0);
        setMcqLimit(limit);

       const getStoredId = (rawKey) => {
          const raw = localStorage.getItem(rawKey);
          if (!raw) return null;
          try {
            const parsed = JSON.parse(raw);
            if (parsed && typeof parsed === 'object' && parsed.id != null) return parsed.id;
            // If parsed is a number/string ID, return that
            return parsed;
          } catch {
            // not JSON, return raw string (likely an id string)
            return raw;
          }
        };
        // Get selected board/grade/subject
        const boardId = getStoredId('selectedBoardId') || getStoredId('selectedBoard') || getStoredId('board_id') || getStoredId('board');
        const gradeId = getStoredId('selectedGradeId') || getStoredId('selectedGrade') || getStoredId('grade_id') || getStoredId('grade');
        const subjectId = getStoredId('selectedSubjectId') || getStoredId('selectedSubject') || getStoredId('subject_id') || getStoredId('subject');

        if (!boardId || !gradeId || !subjectId) {
          toast.error("Missing selected board/grade/subject. Please go back and select.");
          return;
        }

        const res = await axios.post(
          `${apiBase}/papers/fetch-questions`,
          { board: Number(boardId), grade: Number(gradeId), subject: Number(subjectId) },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const data = res.data || {};
        const mcqQs = [];
        Object.entries(data).forEach(([chapterId, chapterData]) => {
          const types = chapterData.types || {};
          Object.entries(types).forEach(([typeLabel, qArray]) => {
            const normalized = String(typeLabel).replace(/\./g, "").toLowerCase();
            if (normalized === "mcq") {
              (Array.isArray(qArray) ? qArray : []).forEach((q) => {
                const chapter_id = q.chapter_id ?? Number(chapterId);
                let options = [];
                try {
                  if (Array.isArray(q.options)) {
                    options = q.options;
                  } else if (typeof q.options === "string") {
                    options = JSON.parse(q.options);
                  }
                } catch (e) {
                  console.warn("Failed to parse options:", q.options);
                }
                mcqQs.push({ ...q, options, chapter_id });
              });
            }
          });
        });

        setQuestions(mcqQs);

        // restore selected from localStorage
        const savedByChapter = JSON.parse(localStorage.getItem(LOCAL_SELECTED_KEY) || "{}");
        const selectedMap = {};
        Object.entries(savedByChapter || {}).forEach(([ch, types]) => {
          const arr = types?.mcq || [];
          (Array.isArray(arr) ? arr : []).forEach((id) => {
            selectedMap[Number(id)] = true;
          });
        });

        // only restore if limit > 0
        if (limit > 0) {
          setSelectedQuestions(selectedMap);
        }
      } catch (error) {
        console.error("Error fetching MCQ questions:", error.response?.data || error);
        toast.error("Failed to fetch questions.");
      }
    };

    fetchQuestions();
  }, []);

  // Handle selecting a question checkbox
  const handleSelectQuestion = async (questionId, chapterId) => {
    const alreadySelected = !!selectedQuestions[questionId];
    const currentCount = Object.keys(selectedQuestions).length;

    if (!mcqLimit) {
      toast.error("No MCQ limit set. Please go back and set question counts.");
      return;
    }

    if (!alreadySelected && currentCount >= mcqLimit) {
      toast.error(`You can only select up to ${mcqLimit} MCQ questions.`);
      return;
    }

    // Update local state
    setSelectedQuestions((prev) => {
      const next = { ...prev };
      if (alreadySelected) {
        delete next[questionId]; // remove
      } else {
        next[questionId] = true; // add
      }
      return next;
    });

    try {
      // --- Update localStorage in nested format ---
      const saved = JSON.parse(localStorage.getItem(LOCAL_SELECTED_KEY) || "{}");
      const chapterKey = String(chapterId);

      if (!saved[chapterKey]) saved[chapterKey] = {};
      if (!Array.isArray(saved[chapterKey].mcq)) saved[chapterKey].mcq = [];

      let arr = saved[chapterKey].mcq;

      if (alreadySelected) {
        arr = arr.filter((x) => Number(x) !== Number(questionId));
      } else {
        arr.push(Number(questionId));
      }

      saved[chapterKey].mcq = arr;
      localStorage.setItem(LOCAL_SELECTED_KEY, JSON.stringify(saved));

      // --- Save via API ---
      const token = localStorage.getItem("token");
      await axios.post(
        `${apiBase}/papers/save-questions`,
        { chapter_id: Number(chapterId), type: "mcq", selected_questions: arr },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Selection updated");
    } catch (error) {
      console.error("Error saving selected MCQ:", error.response?.data || error);
      toast.error("Failed to save selection");
    }
  };

  // Handle profile save
  const handleSaveProfile = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${apiBase}/update-profile`,
        { name: user.name, password: user.password },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Profile updated successfully!");
      setIsModalOpen(false);
      setUser({ ...user, password: "" });
    } catch (error) {
      console.error("Error updating profile:", error.response?.data || error);
      toast.error("Failed to update profile");
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("click", handleClickOutside, true);
    return () => document.removeEventListener("click", handleClickOutside, true);
  }, []);

  return (
    <div className="dashboard-container">
      <Sidebar
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
        activeItem={activeItem}
        setActiveItem={setActiveItem}
      />

      <main className="main-content">
        <div className="mcq-header">
          <div className="mcq-welcome-text">
            <a onClick={() => navigate(-1)} className="back-link">
              <i className="fa fa-arrow-left" aria-hidden="true"></i>
            </a>
            <span className="mcq-welcome-text">M.C.Q</span>
          </div>

          <div className="header-icons" ref={dropdownRef}>
            <FaBell className="icon" />
            <div className="user-dropdown-wrapper">
              <FaUserCircle className="icon" onClick={toggleDropdown} />
              {showDropdown && (
                <div className="user-dropdown">
                  <div className="edituser" onClick={() => setIsModalOpen(true)}>
                    <FaUserCircle className="ditusericon" />
                    <span>Edit Profile</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mcq-questions-container">
          {questions.length > 0 ? (
            questions.map((q, index) => (
              <div key={q.id} className="mcq-question-box">
                <div className="mcq-question">
                  <input
                    type="checkbox"
                    className="mcq-checkbox"
                    checked={!!selectedQuestions[q.id]}
                    onChange={() => handleSelectQuestion(q.id, q.chapter_id)}
                  />
                  <span
                    className="question-text"
                    dangerouslySetInnerHTML={{
                      __html: `${index + 1}. ${String(q.question || q.question_title || "")
                        .replace(/<\/?p>/g, "")}`,
                    }}
                  />
                </div>
                <div className="mcq-options">
                  {q.options &&
                    q.options.map((opt, i) => {
                      const cleanOpt = String(opt).replace(/<\/?[^>]+(>|$)/g, "");
                      return (
                        <div key={i}>
                          {String.fromCharCode(65 + i)}. {cleanOpt}
                        </div>
                      );
                    })}
                </div>
              </div>
            ))
          ) : (
            <div className="no-questions-container">
              <div className="no-questions-icon">📄</div>
              <h3>No MCQ Questions Found</h3>
              <p style={{ color: "#666" }}>
                There are currently no multiple choice questions for this Board / Grade / Subject.
              </p>
            </div>
          )}
        </div>
      </main>

      {/* Profile Edit Modal */}
      {isModalOpen && (
        <div style={modalStyles.overlay}>
          <div style={modalStyles.modal}>
            <button style={modalStyles.closeBtn} onClick={() => setIsModalOpen(false)}>
              &times;
            </button>
            <div style={modalStyles.userIcon}>
              <FaUserCircle />
            </div>

            <div style={modalStyles.formGroup}>
              <label>Username</label>
              <input
                type="text"
                value={user.name}
                onChange={(e) => setUser({ ...user, name: e.target.value })}
                style={modalStyles.input}
              />
            </div>

            <div style={modalStyles.formGroup}>
              <label>Email</label>
              <input
                type="email"
                value={user.email}
                disabled
                style={{ ...modalStyles.input, background: "#f0f0f0", color: "#888" }}
              />
            </div>

            <div style={modalStyles.formGroup}>
              <label>Password</label>
              <input
                type="password"
                value={user.password}
                onChange={(e) => setUser({ ...user, password: e.target.value })}
                style={modalStyles.input}
              />
            </div>

            <div style={{ textAlign: "right" }}>
              <button style={modalStyles.saveBtn} onClick={handleSaveProfile}>
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Modal inline styles
const modalStyles = {
  overlay: {
    position: "fixed",
    top: 0, left: 0, width: "100%", height: "100%",
    background: "rgba(0,0,0,0.6)", display: "flex",
    justifyContent: "center", alignItems: "center", zIndex: 1000,
  },
  modal: {
    background: "#fff", padding: "2rem", borderRadius: "12px",
    width: "90%", maxWidth: "400px", position: "relative",
    boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
  },
  closeBtn: {
    position: "absolute", top: "10px", right: "15px",
    background: "none", border: "none", fontSize: "1.5rem", cursor: "pointer",
  },
  userIcon: {
    textAlign: "center", fontSize: "4rem", color: "#129255", marginBottom: "1rem",
  },
  formGroup: { marginBottom: "1rem" },
  input: {
    width: "100%", padding: "0.5rem 0.8rem", borderRadius: "6px",
    border: "1px solid #ccc", outline: "none",
  },
  saveBtn: {
    background: "#129255", color: "#fff", border: "none",
    padding: "0.6rem 1.2rem", borderRadius: "6px", cursor: "pointer", fontWeight: "bold",
  },
};

export default Mcq;
